import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { 
  Music, Trophy, Calendar, Sparkles, PenTool, Users,
  ArrowRight, Loader2, ChevronRight
} from 'lucide-react';
import { usePageVisibility } from '../hooks/usePageVisibility';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const categoryData = {
  artist: {
    icon: Music,
    name: 'Artiste de l\'année',
    breton: 'Arzour ar Bloaz',
    description: 'Récompense l\'artiste ou le groupe qui a marqué l\'année par son talent, ses performances et son impact sur la scène musicale bretonne.',
    criteria: ['Qualité artistique', 'Rayonnement', 'Activité scénique', 'Innovation'],
    color: 'gold'
  },
  album: {
    icon: Trophy,
    name: 'Album de l\'année',
    breton: 'Albom ar Bloaz',
    description: 'Célèbre le meilleur album sorti dans l\'année, qu\'il soit traditionnel, contemporain ou fusion.',
    criteria: ['Qualité musicale', 'Production', 'Originalité', 'Cohérence artistique'],
    color: 'electric-cyan'
  },
  festival: {
    icon: Calendar,
    name: 'Festival de l\'année',
    breton: 'Gouel ar Bloaz',
    description: 'Honore le festival qui a su créer une expérience mémorable et promouvoir la musique bretonne.',
    criteria: ['Programmation', 'Ambiance', 'Organisation', 'Accessibilité'],
    color: 'green-400'
  },
  young: {
    icon: Sparkles,
    name: 'Meilleur jeune espoir',
    breton: 'Spi Nevez ar Bloaz',
    description: 'Met en lumière les talents émergents qui représentent l\'avenir de la musique bretonne.',
    criteria: ['Potentiel artistique', 'Originalité', 'Engagement', 'Progression'],
    color: 'purple-400'
  },
  composer: {
    icon: PenTool,
    name: 'Compositeur de l\'année',
    breton: 'Aozer ar Bloaz',
    description: 'Récompense l\'excellence dans la composition musicale, qu\'elle soit pour soi ou pour d\'autres artistes.',
    criteria: ['Créativité', 'Technique', 'Émotion', 'Diversité'],
    color: 'orange-400'
  },
  bagad: {
    icon: Users,
    name: 'Titre de l\'année',
    breton: 'Bagad ar Bloaz',
    description: 'Célèbre l\'ensemble de musique traditionnelle bretonne qui s\'est le plus distingué dans l\'année.',
    criteria: ['Performance musicale', 'Cohésion', 'Répertoire', 'Présence scénique'],
    color: 'blue-400'
  }
};

const Categories = () => {
  const [nominations, setNominations] = useState({});
  const [loading, setLoading] = useState(true);
  usePageVisibility('page_categories_enabled');

  useEffect(() => {
    fetchNominations();
  }, []);

  const fetchNominations = async () => {
    try {
      const response = await axios.get(`${API}/nominations?approved_only=true`);
      const grouped = {};
      response.data.forEach(nom => {
        if (!grouped[nom.category_id]) {
          grouped[nom.category_id] = [];
        }
        grouped[nom.category_id].push(nom);
      });
      setNominations(grouped);
    } catch (error) {
      console.error('Error fetching nominations:', error);
    } finally {
      setLoading(false);
    }
  };

  const categoryIds = Object.keys(categoryData);

  return (
    <main className="min-h-screen bg-obsidian pt-32 pb-20" data-testid="categories-page">
      {/* Hero */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 mb-16">
        <div className="max-w-3xl">
          <span className="text-gold text-sm uppercase tracking-widest">Les Récompenses</span>
          <h1 className="font-syne text-4xl md:text-6xl font-extrabold tracking-tighter text-white mt-4 mb-6">
            6 Catégories<br />
            <span className="text-gold-gradient">d'Excellence</span>
          </h1>
          <p className="text-xl text-white/60">
            Six domaines pour célébrer toutes les facettes de la musique bretonne. 
            Artistes, albums, festivals, jeunes talents, compositeurs et bagadoù.
          </p>
        </div>
      </section>

      {/* Categories Grid */}
      <section className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="space-y-12">
          {categoryIds.map((catId, idx) => {
            const cat = categoryData[catId];
            const Icon = cat.icon;
            const catNominations = nominations[catId] || [];
            
            return (
              <div 
                key={catId} 
                id={catId}
                className="scroll-mt-32"
              >
                <div className={`border border-white/10 bg-obsidian-light overflow-hidden ${idx % 2 === 0 ? '' : ''}`}>
                  {/* Header */}
                  <div className="p-8 border-b border-white/10">
                    <div className="flex flex-col md:flex-row md:items-center gap-6">
                      <div className={`w-20 h-20 flex items-center justify-center border border-${cat.color}/30 bg-${cat.color}/5 flex-shrink-0`}>
                        <Icon className={`w-10 h-10 text-${cat.color}`} />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h2 className="font-syne text-2xl md:text-3xl font-bold text-white">{cat.name}</h2>
                          <span className="px-3 py-1 bg-white/5 text-xs text-white/50 uppercase tracking-wider">
                            {catNominations.length} propositions
                          </span>
                        </div>
                        <p className="text-sm text-gold italic">{cat.breton}</p>
                      </div>
                      <Link
                        to={`/proposer?category=${catId}`}
                        className="inline-flex items-center gap-2 px-6 py-3 bg-gold hover:bg-gold-glow text-obsidian font-bold uppercase tracking-wider text-sm transition-all flex-shrink-0"
                      >
                        Proposer <ArrowRight className="w-4 h-4" />
                      </Link>
                    </div>
                  </div>
                  
                  {/* Content */}
                  <div className="p-8">
                    <p className="text-white/60 mb-6">{cat.description}</p>
                    
                    <div className="mb-8">
                      <p className="text-sm text-white/40 uppercase tracking-wider mb-3">Critères d'évaluation</p>
                      <div className="flex flex-wrap gap-2">
                        {cat.criteria.map((criterion, cidx) => (
                          <span key={cidx} className="px-3 py-1 border border-white/10 text-sm text-white/70">
                            {criterion}
                          </span>
                        ))}
                      </div>
                    </div>
                    
                    {/* Top Nominations */}
                    {catNominations.length > 0 && (
                      <div>
                        <p className="text-sm text-white/40 uppercase tracking-wider mb-4">Top propositions</p>
                        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                          {catNominations.slice(0, 4).map((nom) => (
                            <div key={nom.id} className="p-4 border border-white/5 bg-obsidian hover:border-gold/30 transition-all">
                              <p className="font-syne font-bold text-white truncate">{nom.nominee_name}</p>
                              <p className="text-sm text-gold">{nom.proposal_count} votes</p>
                            </div>
                          ))}
                        </div>
                        {catNominations.length > 4 && (
                          <Link to={`/proposer?category=${catId}`} className="inline-flex items-center gap-1 text-gold text-sm mt-4 hover:text-gold-glow">
                            Voir toutes les propositions <ChevronRight className="w-4 h-4" />
                          </Link>
                        )}
                      </div>
                    )}
                    
                    {catNominations.length === 0 && (
                      <div className="text-center py-8 border border-dashed border-white/10">
                        <p className="text-white/40 mb-4">Aucune proposition dans cette catégorie</p>
                        <Link
                          to={`/proposer?category=${catId}`}
                          className="inline-flex items-center gap-2 text-gold hover:text-gold-glow"
                        >
                          Soyez le premier à proposer <ArrowRight className="w-4 h-4" />
                        </Link>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 mt-16">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <h2 className="font-syne text-3xl md:text-4xl font-bold text-white mb-6">
            Votre favori n'est pas là ?
          </h2>
          <p className="text-white/60 max-w-xl mx-auto mb-10">
            Proposez les artistes, albums et festivals qui méritent d'être reconnus.
          </p>
          <Link
            to="/proposer"
            className="bg-gold hover:bg-gold-glow text-obsidian font-bold uppercase tracking-widest px-8 py-4 text-sm transition-all inline-flex items-center gap-2"
          >
            Proposer maintenant <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
    </main>
  );
};

export default Categories;
